import * as React from 'react';
import { Portal, Transition, Popover as Popover$1 } from '@headlessui/react';
import { usePopper } from 'react-popper';
import clsx from 'clsx';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

/**
 * Creates a named context, provider, and hook.
 *
 * @param options create context options
 */

function createContext(options) {
  if (options === void 0) {
    options = {};
  }

  var _a = options.strict,
      strict = _a === void 0 ? true : _a,
      _b = options.errorMessage,
      errorMessage = _b === void 0 ? 'useContext: `context` is undefined. Seems you forgot to wrap component within the Provider' : _b,
      name = options.name;
  var Context = /*#__PURE__*/React.createContext(undefined);
  Context.displayName = name;

  function useContext() {
    var _a;

    var context = React.useContext(Context);

    if (!context && strict) {
      var error = new Error(errorMessage);
      error.name = 'ContextError';
      (_a = Error.captureStackTrace) === null || _a === void 0 ? void 0 : _a.call(Error, error, useContext);
      throw error;
    }

    return context;
  }

  return [Context.Provider, useContext, Context];
}

var _a;
var PopoverContextProvider = (_a = createContext(), _a[0]), usePopoverContext = _a[1];

function styleInject(css, ref) {
  if (ref === void 0) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') {
    return;
  }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z = ".Popover-module_popoverContent__2sQoY{transform-origin:top right;border-radius:.375rem;background-color:var(--color-white);outline:2px solid transparent;outline-offset:2px;z-index:10;box-shadow:0 0 0 0 #fff,0 0 0 1px rgba(0,0,0,.05),0 10px 15px -3px rgba(0,0,0,.1),0 4px 6px -2px rgba(0,0,0,.05)}.Popover-module_popoverContentContainer__2mPVi{padding:.25rem .5rem}.Popover-module_transitionEnter__3Yv3d{transition-property:background-color,border-color,color,fill,stroke,opacity,box-shadow,transform,filter,-webkit-backdrop-filter;transition-property:background-color,border-color,color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter;transition-property:background-color,border-color,color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter,-webkit-backdrop-filter;transition-timing-function:cubic-bezier(0,0,.2,1);transition-duration:.1s}.Popover-module_transitionEnterFrom__EXI7R{transform:scale(.95);opacity:0}.Popover-module_transitionEnterTo__J5l19{transform:scale(1);opacity:1}.Popover-module_transitionLeave__3LneP{transition-property:background-color,border-color,color,fill,stroke,opacity,box-shadow,transform,filter,-webkit-backdrop-filter;transition-property:background-color,border-color,color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter;transition-property:background-color,border-color,color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter,-webkit-backdrop-filter;transition-timing-function:cubic-bezier(.4,0,1,1);transition-duration:75ms}.Popover-module_transitionLeaveFrom__WR3Gj{transform:scale(1);opacity:1}.Popover-module_transitionLeaveTo__D20Bk{transform:scale(.95);opacity:0}";
var styles = {"popoverContent":"Popover-module_popoverContent__2sQoY","popoverContentContainer":"Popover-module_popoverContentContainer__2mPVi","transitionEnter":"Popover-module_transitionEnter__3Yv3d","transitionEnterFrom":"Popover-module_transitionEnterFrom__EXI7R","transitionEnterTo":"Popover-module_transitionEnterTo__J5l19","transitionLeave":"Popover-module_transitionLeave__3LneP","transitionLeaveFrom":"Popover-module_transitionLeaveFrom__WR3Gj","transitionLeaveTo":"Popover-module_transitionLeaveTo__D20Bk"};
styleInject(css_248z);

var DESCRIPTION_NAME$1 = 'Div';
var PopoverContent = React.forwardRef(function (_a, ref) {
    var className = _a.className, children = _a.children, props = __rest(_a, ["className", "children"]);
    var _b = usePopoverContext(), popperStyles = _b.popperStyles, popperAttributes = _b.popperAttributes, setPopperElement = _b.setPopperElement, isOpen = _b.isOpen;
    var classes = clsx(styles.popoverContent, className);
    return (React.createElement(Portal, null,
        React.createElement("div", __assign({ ref: ref, style: __assign(__assign({}, popperStyles.popper), { zIndex: '50' }) }, popperAttributes.popper),
            React.createElement(Transition, { show: isOpen, as: React.Fragment, enter: styles.transitionEnter, enterFrom: styles.transitionEnterFrom, enterTo: styles.transitionEnterTo, leave: styles.transitionLeave, leaveFrom: styles.transitionLeaveFrom, leaveTo: styles.transitionLeaveTo, beforeEnter: function () { var _a; return setPopperElement((_a = ref) === null || _a === void 0 ? void 0 : _a.current); }, afterLeave: function () { return setPopperElement(null); } },
                React.createElement(Popover$1.Panel, __assign({ static: true, className: classes }, props),
                    React.createElement("div", { className: styles.popoverContentContainer }, children))))));
});
PopoverContent.displayName = DESCRIPTION_NAME$1;

var DESCRIPTION_NAME = 'Button';
var PopoverTrigger = React.forwardRef(function (_a, ref) {
    var children = _a.children, className = _a.className, props = __rest(_a, ["children", "className"]);
    var classes = clsx(styles.popoverTrigger, className);
    return (React.createElement(Popover$1.Button, __assign({ className: classes }, props), children));
});
PopoverTrigger.displayName = DESCRIPTION_NAME;

var OpenListener = function (_a) {
    var open = _a.open;
    var setIsOpen = usePopoverContext().setIsOpen;
    React.useEffect(function () {
        setIsOpen(open);
    }, [open, setIsOpen]);
    return React.createElement("div", null);
};
var Popover = function (_a) {
    var children = _a.children; _a.className; var _b = _a.placement, placement = _b === void 0 ? 'bottom-end' : _b, _c = _a.offset, offset = _c === void 0 ? [0, 0] : _c, onClose = _a.onClose, onOpen = _a.onOpen, props = __rest(_a, ["children", "className", "placement", "offset", "onClose", "onOpen"]);
    var PopperElRef = React.useRef();
    var _d = React.useState(null), targetElement = _d[0], setTargetElement = _d[1];
    var _e = React.useState(null), popperElement = _e[0], setPopperElement = _e[1];
    var _f = React.useState(false), isOpen = _f[0], setIsOpen = _f[1];
    var _g = React.useState(false), isRendered = _g[0], setIsRendered = _g[1];
    var _h = usePopper(targetElement, popperElement, {
        placement: placement,
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: offset,
                },
            },
        ],
    }), popperStyles = _h.styles, popperAttributes = _h.attributes;
    React.useEffect(function () {
        if (isRendered) {
            if (isOpen && onOpen) {
                onOpen();
            }
            if (!isOpen && onClose) {
                onClose();
            }
        }
        else {
            setIsRendered(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, onClose, onOpen]);
    return (React.createElement(PopoverContextProvider, { value: {
            popperStyles: popperStyles,
            popperAttributes: popperAttributes,
            setPopperElement: setPopperElement,
            isOpen: isOpen,
            setIsOpen: setIsOpen,
        } },
        React.createElement("div", __assign({ ref: setTargetElement }, props),
            React.createElement(Popover$1, { as: "div" }, function (_a) {
                var open = _a.open;
                return (React.createElement(React.Fragment, null,
                    React.createElement(OpenListener, { open: open }),
                    React.Children.toArray(children).map(function (child, index) {
                        if (typeof child === 'string') {
                            return child;
                        }
                        return React.cloneElement(child, __assign({ key: index }, (child.type === PopoverContent
                            ? {
                                ref: PopperElRef,
                            }
                            : {})));
                    })));
            }))));
};

export { Popover, PopoverContent, PopoverContextProvider, PopoverTrigger, usePopoverContext };
